import { useEffect, useMemo, useState } from "react";
import {
  Globe,
  Download,
  MessageCircle,
  Linkedin,
  Twitter,
  Mail,
  Link2,
  QrCode,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  fetchReferralSources,
  sourcesToCsv,
  type SourceRow,
} from "@/lib/referral";

type Range = 7 | 30 | 90;
type Metric = "clicks" | "signups" | "conversions";

const METRIC_LABEL: Record<Metric, string> = {
  clicks: "Clicks",
  signups: "Signups",
  conversions: "Conversions",
};

const SOURCE_META: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }>; tone: string }
> = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, tone: "text-emerald-500" },
  linkedin: { label: "LinkedIn", icon: Linkedin, tone: "text-sky-500" },
  twitter: { label: "X / Twitter", icon: Twitter, tone: "text-foreground" },
  email: { label: "Email", icon: Mail, tone: "text-amber-500" },
  copy: { label: "Copied link", icon: Link2, tone: "text-primary" },
  qr: { label: "QR poster", icon: QrCode, tone: "text-violet-500" },
  direct: { label: "Direct / unknown", icon: HelpCircle, tone: "text-muted-foreground" },
};

function metaFor(source: string) {
  return (
    SOURCE_META[source.toLowerCase()] ?? {
      label: source,
      icon: Globe,
      tone: "text-muted-foreground",
    }
  );
}

export default function SourcesPanel() {
  const [days, setDays] = useState<Range>(30);
  const [rows, setRows] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<Metric>("clicks");

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchReferralSources(days).then((r) => {
      if (!active) return;
      setRows(r);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [days]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => b[metric] - a[metric]);
  }, [rows, metric]);

  const max = sorted[0]?.[metric] ?? 0;
  const total = sorted.reduce((s, r) => s + r[metric], 0);

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.error("Nothing to export yet.");
      return;
    }
    const blob = new Blob([sourcesToCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wekit-referral-sources-${days}d-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sources CSV downloaded");
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-foreground flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" /> Top sources
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Where your clicks, signups, and conversions are coming from.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v) as Range)}>
              <TabsList className="h-8">
                <TabsTrigger value="7" className="text-xs px-2.5 h-6">7d</TabsTrigger>
                <TabsTrigger value="30" className="text-xs px-2.5 h-6">30d</TabsTrigger>
                <TabsTrigger value="90" className="text-xs px-2.5 h-6">90d</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button size="sm" variant="outline" onClick={exportCsv} className="gap-1.5 h-8">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </div>

        <Tabs value={metric} onValueChange={(v) => setMetric(v as Metric)}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="clicks" className="text-xs">Clicks</TabsTrigger>
            <TabsTrigger value="signups" className="text-xs">Signups</TabsTrigger>
            <TabsTrigger value="conversions" className="text-xs">Conversions</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 || total === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No {METRIC_LABEL[metric].toLowerCase()} attributed to a source yet. Share
            your link via WhatsApp, LinkedIn or Email and the breakdown will appear
            here.
          </p>
        ) : (
          <ul className="space-y-2">
            {sorted.slice(0, 8).map((row, idx) => {
              const meta = metaFor(row.source);
              const Icon = meta.icon;
              const value = row[metric];
              const pct = max > 0 ? (value / max) * 100 : 0;
              const share = total > 0 ? (value / total) * 100 : 0;
              return (
                <li
                  key={row.source}
                  className="relative rounded-lg border border-border bg-background/40 p-3 overflow-hidden"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/10"
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[11px] font-mono text-muted-foreground w-4 text-right">
                        {idx + 1}
                      </span>
                      <Icon className={`h-4 w-4 shrink-0 ${meta.tone}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {meta.label}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {row.clicks} clicks · {row.signups} signups ·{" "}
                          {row.conversions} conversions
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display text-lg font-bold tabular-nums text-foreground leading-none">
                        {value.toLocaleString()}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {share.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] text-muted-foreground">
          Signups & conversions are attributed to the most recent click source for
          the same invite link.
        </p>
      </CardContent>
    </Card>
  );
}
